import 'reflect-metadata';
import './main.css';

import { registerSW } from 'virtual:pwa-register';
import { Capacitor } from '@capacitor/core';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import * as Sentry from '@sentry/capacitor';
import * as SentryReact from '@sentry/react';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { type Query, QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { PostHogProvider } from 'posthog-js/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { baseClient } from '@merchant-clients/common';

import { App } from '@frontend/app';

import { initAnalytics } from './analytics';
import { getAuthToken, setAuthToken } from './auth';
import { MerchantPublicConfigurationProvider } from './merchant-public-configuration';
import { KeyValueStore } from './utils';
import { ErrorView } from './views';

KeepAwake.isSupported().then(({ isSupported }) => {
  if (isSupported) {
    KeepAwake.keepAwake().catch((error) => {
      console.error('Error enabling KeepAwake', error);
    });
  }
});

CapacitorUpdater.notifyAppReady().catch((error) => {
  console.error(error);
});

if (!Capacitor.isNativePlatform()) {
  const updateSW = registerSW({
    onNeedRefresh() {
      updateSW();
    },
  });
}

baseClient.setBaseUrl(APP_CONFIG.baseUrl, APP_CONFIG.baseHeaders);

baseClient.setStorageEngine({
  getToken: getAuthToken,
  setToken: setAuthToken,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 604800000, // 7 days
    },
  },
});

const persister = createAsyncStoragePersister({
  storage: {
    getItem: async (key) => {
      return await KeyValueStore.get(key);
    },
    setItem: async (key, value) => {
      await KeyValueStore.set(key, value);
    },
    removeItem: async (key) => {
      await KeyValueStore.remove(key);
    },
  },
});

const posthogClient = initAnalytics();

Sentry.init(
  {
    ...APP_CONFIG.sentry.initConfig,
    environment: APP_CONFIG.environment,
    integrations: [
      posthogClient.sentryIntegration({
        organization: APP_CONFIG.sentry.organization,
        projectId: APP_CONFIG.sentry.projectId,
        severityAllowList: ['error', 'info'],
      }),
    ],
  },
  SentryReact.init as any,
);

Sentry.setTags({
  stack: APP_CONFIG.stack,
});

createRoot(document.querySelector('#root')! as HTMLElement, {
  onCaughtError: SentryReact.reactErrorHandler(),
  onRecoverableError: SentryReact.reactErrorHandler(),
  onUncaughtError: SentryReact.reactErrorHandler((error, errorInfo) => {
    console.error('Uncaught error', error, errorInfo.componentStack);
  }),
}).render(
  <StrictMode>
    <PostHogProvider client={posthogClient}>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          dehydrateOptions: {
            shouldDehydrateQuery: (query: Query) =>
              query.state.status === 'success' &&
              !query.queryKey.includes('verify-session'),
          },
        }}
      >
        <MerchantPublicConfigurationProvider>
          <SentryReact.ErrorBoundary
            fallback={<ErrorView />}
            onError={console.error}
          >
            <App />
          </SentryReact.ErrorBoundary>
        </MerchantPublicConfigurationProvider>
      </PersistQueryClientProvider>
    </PostHogProvider>
  </StrictMode>,
);
